import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n";
import { buildTrackingCartAttributes } from "@/stores/cartStore";

// Shopify Configuration
const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'glow-up-labs-g6fzu.myshopify.com';
// NOTE: We proxy Storefront requests through a backend function so the Storefront token is not exposed in client code.
const SHOPIFY_STOREFRONT_PROXY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-storefront`;

// Types
export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    compareAtPriceRange?: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice?: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
    tags: string[];
    productType: string;
  };
}

// Storefront API helper
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // The backend function is public; these headers identify the project.
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`
      },
      body: JSON.stringify({
        query,
        variables,
        // Not strictly required, but makes debugging and future routing easier.
        shopDomain: SHOPIFY_STORE_PERMANENT_DOMAIN,
        apiVersion: SHOPIFY_API_VERSION,
      }),
    });

    if (response.status === 402) {
      toast.error("Shopify: Payment required", {
        description: "Shopify API access requires an active billing plan."
      });
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(`Shopify error: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
    }

    return data;
  } catch (error) {
    console.error('Storefront API error:', error);
    throw error;
  }
}

// GraphQL Queries
export const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          tags
          productType
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      tags
      productType
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 20) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

// Cart Mutations
export const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        attributes {
          key
          value
        }
        lines(first: 100) { 
          edges { 
            node { 
              id 
              merchandise { 
                ... on ProductVariant { id } 
              } 
            } 
          } 
        }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) { 
          edges { 
            node { 
              id 
              merchandise { 
                ... on ProductVariant { id } 
              } 
            } 
          } 
        }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { id }
      userErrors { field message }
    }
  }
`;

export const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      totalQuantity
      cost {
        totalAmount { amount currencyCode }
        subtotalAmount { amount currencyCode }
      }
      discountAllocations {
        discountedAmount { amount currencyCode }
      }
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant { id }
            }
            cost {
              subtotalAmount { amount currencyCode }
              totalAmount { amount currencyCode }
            }
          }
        }
      }
    }
  }
`;

export const CART_DISCOUNT_CODES_UPDATE = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        discountCodes {
          code
          applicable
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors { field message }
    }
  }
`;

// Cart Attributes Update (for cross-domain FB tracking bridge)
export const CART_ATTRIBUTES_UPDATE = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart { id }
      userErrors { field message }
    }
  }
`;

// Compare-at (original) prices mapped by product handle (in CAD)
export const COMPARE_AT_PRICES: Record<string, { amount: string; currencyCode: string }> = {
  'pdrn-pink-sheet-masks-10-pack': { amount: '59.99', currencyCode: 'CAD' },
  'pink-glow-serum-with-pdrn-peptides': { amount: '65.99', currencyCode: 'CAD' },
  'smart-capsule-cream-with-collagen': { amount: '68.39', currencyCode: 'CAD' },
  'complete-pdrn-trio-bundle': { amount: '191.99', currencyCode: 'CAD' },
};

// Get compare-at price for a product (Shopify data takes priority, then fallback map)
export function getCompareAtPrice(
  handle: string,
  shopifyCompareAt?: { amount: string; currencyCode: string } | null
): { amount: string; currencyCode: string } | null {
  if (shopifyCompareAt && parseFloat(shopifyCompareAt.amount) > 0) {
    return shopifyCompareAt;
  }
  return COMPARE_AT_PRICES[handle] || null;
}

// Calculate discount percentage between two prices
export function calcDiscountPercent(saleAmount: number, originalAmount: number): number {
  if (originalAmount <= 0 || saleAmount >= originalAmount) return 0;
  return Math.round(((originalAmount - saleAmount) / originalAmount) * 100);
}

// Approximate exchange rates to USD (base)
const EXCHANGE_RATES_TO_USD: Record<string, number> = {
  USD: 1,
  CAD: 0.74,
  EUR: 1.08,
  KRW: 0.00074,
  ILS: 0.28,
};

// Round up to next number ending in 9 (psychological pricing)
// e.g. 185.98 → 189, 103 → 109, 42 → 49, 191 → 199
function roundUpToNine(n: number): number {
  const rounded = Math.ceil(n);           // e.g. 185.98 → 186
  const lastDigit = rounded % 10;         // e.g. 6
  if (lastDigit === 9) return rounded;    // already ends in 9
  if (lastDigit < 9) return rounded + (9 - lastDigit); // 186 → 189
  return rounded + 9;                     // shouldn't happen but safety
}

// Raw currency conversion (no psychological rounding) – for calculations like remaining amounts
export function convertPriceRaw(amount: number, fromCurrency: string, toCurrency: string): number {
  if (fromCurrency === toCurrency) return amount;
  const fromRate = EXCHANGE_RATES_TO_USD[fromCurrency] || 1;
  const toRate = EXCHANGE_RATES_TO_USD[toCurrency] || 1;
  return (amount * fromRate) / toRate;
}

// Convert from one currency to another with psychological rounding
export function convertPrice(amount: number, fromCurrency: string, toCurrency: string): number {
  if (fromCurrency === toCurrency) return amount;
  
  const converted = convertPriceRaw(amount, fromCurrency, toCurrency);
  
  // KRW: round up to nearest 1000 ending in 900 (e.g. 34500 → 34900)
  if (toCurrency === 'KRW') {
    const nearestThousand = Math.ceil(converted / 1000) * 1000;
    return nearestThousand - 100; // e.g. 35000 → 34900
  }
  
  // All other currencies: round up to end in 9 (no decimals)
  return roundUpToNine(converted);
}

// Helper functions
export function formatCheckoutUrl(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set('channel', 'online_store');

    // Pass user's language to Shopify checkout
    const language = useLanguage.getState().language;
    const shopifyLocale = language === 'he' ? 'he' : 'en';
    url.searchParams.set('locale', shopifyLocale);

    // Forward fbclid for Meta attribution across Shopify checkout
    const fbclid = localStorage.getItem('_fb_last_fbclid');
    if (fbclid) url.searchParams.set('fbclid', fbclid);

    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

export function formatPrice(amount: string, currencyCode: string, targetCurrency?: string): string {
  let num = parseFloat(amount);
  let displayCurrency = currencyCode;
  const isConverted = targetCurrency && targetCurrency !== currencyCode;
  
  if (isConverted) {
    num = convertPrice(num, currencyCode, targetCurrency);
    displayCurrency = targetCurrency;
  }
  
  const locale = displayCurrency === 'KRW' ? 'ko-KR'
    : displayCurrency === 'ILS' ? 'he-IL'
    : displayCurrency === 'EUR' ? 'de-DE'
    : displayCurrency === 'CAD' ? 'en-CA'
    : 'en-US';
  
  // Converted prices are whole numbers (psychological pricing), no decimals needed
  const noDecimals = displayCurrency === 'KRW' || isConverted;
  
  let formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: displayCurrency,
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  }).format(num);

  // Ensure AUD shows "AU$" for clarity
  if (displayCurrency === 'AUD') {
    if (formatted.includes('AU$')) {
      // already correct
    } else if (formatted.includes('A$')) {
      formatted = formatted.replace('A$', 'AU$');
    } else {
      formatted = formatted.replace('$', 'AU$');
    }
  }

  // Ensure CAD shows "C$" for clarity
  if (displayCurrency === 'CAD') {
    if (formatted.includes('C$') || formatted.includes('CA$')) {
      // already has prefix
    } else {
      formatted = formatted.replace('$', 'C$');
    }
  }

  return formatted;
}

export function isCartNotFoundError(userErrors: Array<{ field: string[] | null; message: string }>): boolean {
  return userErrors.some(e => 
    e.message.toLowerCase().includes('cart not found') || 
    e.message.toLowerCase().includes('does not exist')
  );
}

// Map display currency → Shopify Markets country code
export const CURRENCY_TO_COUNTRY: Record<string, string> = {
  USD: 'US',
  GBP: 'GB',
  AUD: 'AU',
  ILS: 'IL',
  CAD: 'CA',
  EUR: 'DE',
  NOK: 'NO',
  AED: 'AE',
};

// Create a one-off cart for "Buy Now" and return the checkout URL + cartId
export async function createBuyNowCart(variantId: string, quantity: number, countryCode?: string): Promise<{ checkoutUrl: string; cartId: string } | null> {
  const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: {
      lines: [{ quantity, merchandiseId: variantId }],
      // Include tracking attributes for Identity Bridge — ensures Purchase webhook
      // receives fbc, fbp, external_id, IP, UA, traffic_source for maximum EMQ
      attributes: buildTrackingCartAttributes(),
      ...(countryCode ? { buyerIdentity: { countryCode } } : {}),
    },
  });

  if (data?.data?.cartCreate?.userErrors?.length > 0) {
    console.error('Buy Now cart creation failed:', data.data.cartCreate.userErrors);
    return null;
  }

  const cart = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;

  return { checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), cartId: cart.id };
}

// Fetch all products
export async function fetchProducts(first: number = 20, query?: string): Promise<ShopifyProduct[]> {
  const data = await storefrontApiRequest(PRODUCTS_QUERY, { first, query });
  return data?.data?.products?.edges || [];
}

// Fetch single product by handle
export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct['node'] | null> {
  const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle });
  return data?.data?.productByHandle || null;
}